/**
 * Who an Intake Hub enrollment row belongs to, and whether it is live.
 *
 * Org-level enrollment (GDN-3404) put two kinds of row in the same array:
 *
 * - **Organization rows** carry `user_id` null. They record what the
 *   organization has bought. They are not any provider's journey.
 * - **Per-user rows** carry a provider's id. These are the journeys.
 *
 * On top of that, un-enrolling a service pauses its rows rather than deleting
 * them, so a `paused` per-user row is a journey the organization has stopped
 * paying for. It is not a product, and it is not something the provider can
 * work on.
 *
 * Every provider-scoped question — "is this provider set up?", "what should we
 * materialize?", "which products drive the form?" — needs the same answer, so
 * it lives here rather than being re-derived at each call site.
 */

export interface IntakeEnrollmentScopeRow {
  status?: string | null;
  user_id?: string | null;
}

/** True for a row that belongs to the organization rather than to a provider. */
export function isOrganizationEnrollment(enrollment: IntakeEnrollmentScopeRow): boolean {
  return !enrollment.user_id;
}

/** True for a service the scope has stopped paying for. */
export function isPausedEnrollment(enrollment: IntakeEnrollmentScopeRow): boolean {
  return enrollment.status === 'paused';
}

/**
 * True for a still-active enrollment belonging to this particular provider.
 *
 * The caller's id matters, not merely that the row has one: the practice-admin
 * ('all') dashboard returns the whole organization's rows, so "some row has a
 * user_id" is true there even when the viewer has no journey of their own.
 *
 * The organization test is deliberately `!user_id` rather than
 * `user_id === null`: the backend serializes an organization row as an
 * explicit null today, but a real per-user row always carries a string, so
 * treating an absent field as "not a provider row" is correct under either
 * serialization.
 */
export function isLiveEnrollmentForUser(
  enrollment: IntakeEnrollmentScopeRow,
  userId: string | null | undefined,
): boolean {
  if (!userId) return false;
  return enrollment.user_id === userId && !isPausedEnrollment(enrollment);
}

/** True when this provider has at least one service they can actually work on. */
export function hasLiveProviderEnrollment(
  enrollments: IntakeEnrollmentScopeRow[] | undefined | null,
  userId: string | null | undefined,
): boolean {
  return (enrollments ?? []).some(enrollment => isLiveEnrollmentForUser(enrollment, userId));
}

/**
 * True when the organization is enrolled in anything at all.
 *
 * Whose row it is does not matter here — this answers "has the practice been
 * set up?", which is what the roster writes anchor on. A paused row is not an
 * enrollment, so it does not count.
 */
export function hasLiveOrganizationEnrollment(
  enrollments: IntakeEnrollmentScopeRow[] | undefined | null,
): boolean {
  return (enrollments ?? []).some(enrollment => !isPausedEnrollment(enrollment));
}
